import { createFeatureSelector, createSelector } from '@ngrx/store'
import { ContractCreateFeModel, IContractEditState } from '../../../contract/edit/state/edit.reducer'
import { StoreKeysEnum } from '../../../../../store/store-keys'
import cloneDeep from 'lodash/cloneDeep'
import { toDisplayUnits } from '../../../../../shared/utils/month-year-unit'

export const selectSubcontractEditFeature = createFeatureSelector<Readonly<IContractEditState>>(StoreKeysEnum.SubcontractEditFeature)

export const selectSubcontractEditContract = createSelector(
    selectSubcontractEditFeature,
    (feature): ContractCreateFeModel => (feature?.contract),
)

// ---

export const selectSubcontractEditTenants = createSelector(
    selectSubcontractEditFeature,
    (data) => data?.tenants
)

export const selectSubcontractEditOwners = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.owners),
)
export const selectSubcontractEditOwnersLength = createSelector(
    selectSubcontractEditOwners,
    owners => (owners?.length)
)

export const selectSubcontractEditSelectedContractTemplate = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.selectedContractTemplate),
)

export const selectSubcontractEditContractWithSubjects = createSelector(
    selectSubcontractEditContract,
    selectSubcontractEditOwners,
    selectSubcontractEditTenants,
    selectSubcontractEditSelectedContractTemplate,
    (contract, owners, tenants, selectedContractTemplate) => ({
        contract: cloneDeep(contract),
        owners: cloneDeep(owners),
        tenants: cloneDeep(tenants),
        selectedContractTemplate: cloneDeep(selectedContractTemplate),
    }),
)

export const selectSubcontractEditContractId = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.id),
)

export const selectSubcontractEditContractCpds = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.cpds),
)

export const selectSubcontractEditAddParcelsData = createSelector(
    selectSubcontractEditFeature,
    selectSubcontractEditContractCpds,
    selectSubcontractEditContractId,
    (feature, cpds, contractId) => ({ parcels: feature?.parcels, cpds, contractId }),
)

export const selectContractEditDrupozs = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.drupozs),
)
export const selectContractEditZpvypos = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.zpvypos),
)

export const selectSubcontractEditPrepareSetParcels = createSelector(
    selectSubcontractEditOwners,
    selectContractEditDrupozs,
    selectContractEditZpvypos,
    (owners, drupozs, zpvypos) => ({
        owners,
        drupozs,
        zpvypos,
    })
)

export const selectSubcontractEditContractOwnerIds = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.ownerIds),
)
export const selectSubcontractEditParcels = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.parcels),
)
export const selectSubcontractEditIsLoading = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isLoading),
)

export const selectSubcontractEditContractRentDrupozCollection = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.contractRentDrupozCollection),
)
export const selectSubcontractEditContractRentUnknownCollection = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.contractRentUnknownCollection),
)

export const selectSubcontractEditContractRentType = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.rentType),
)

export const selectComputeContractPriceAndTaxData = createSelector(
    selectSubcontractEditContractId,
    selectSubcontractEditContractOwnerIds,
    selectSubcontractEditParcels,
    selectSubcontractEditIsLoading,
    selectSubcontractEditContractRentDrupozCollection,
    selectSubcontractEditContractRentUnknownCollection,
    selectSubcontractEditContractRentType,
    (contractId, ownerIds, parcels, isLoading, contractRentDrupozCollection, contractRentUnknownCollection, rentType): {
        contractId: number;
        isAnyOwnerIds: boolean;
        isAnyParcels: boolean;
        isLoading: boolean;
        contractRentDrupozCollection: any;
        contractRentUnknownCollection: any;
        rentType: string;
    } => ({
        contractId,
        isAnyOwnerIds: ownerIds?.length > 0,
        isAnyParcels: parcels?.length > 0,
        isLoading,
        contractRentDrupozCollection,
        contractRentUnknownCollection,
        rentType,
    }),
)

export const selectSubcontractEditEditedParcel = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.editedParcel),
)

export const selectSubcontractEditEditedCpd = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.editedCPD),
)

export const selectSubcontractEditEditedCpdAndConsequencies = createSelector(
    selectSubcontractEditEditedCpd,
    selectSubcontractEditContractCpds,
    selectSubcontractEditEditedParcel,
    selectSubcontractEditParcels,
    (editedCPD, cpds, editedParcel, parcels) => ({ editedCPD, cpds, editedParcel, parcels }),
)

export const selectSubcontractEditParcelDelete = createSelector(
    selectSubcontractEditParcels,
    selectSubcontractEditEditedParcel,
    selectSubcontractEditEditedCpd,
    selectSubcontractEditContractCpds,
    (parcels, editedParcel, editedCpd, cpds) => ({
        parcels,
        editedParcel,
        editedCpd,
        cpds,
    }),
)

export const selectSubcontractEditContractIndefinite = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.indefinite),
)

export const selectSubcontractEditIsAppendix = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isAppendix),
)

export const selectSubcontractEditContractAppendixSourceId = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.appendixSourceId),
)

export const selectSubcontractEditIsSuccessive = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isSuccessive),
)

export const selectSubcontractEditIsFormActive = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isFormActive),
)

export const selectSubcontractEditContractSuccessiveSourceId = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.successiveSourceId),
)
export const selectSubcontractEditParamTemplates = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.contractParamTemplates),
)

export const selectSubcontractEditSetContract = createSelector(
    selectSubcontractEditContractIndefinite,
    selectSubcontractEditIsAppendix,
    selectSubcontractEditContractAppendixSourceId,
    selectSubcontractEditIsSuccessive,
    selectSubcontractEditContractSuccessiveSourceId,
    selectSubcontractEditParamTemplates,
    selectSubcontractEditSelectedContractTemplate,
    (indefinite, isAppendix, appendixSourceId, isSuccessive, successiveSourceId, contractParamTemplates, selectedContractTemplate) => ({
        indefinite,
        isAppendix,
        appendixSourceId,
        isSuccessive,
        successiveSourceId,
        contractParamTemplates,
        selectedContractTemplate,
    }),
)

export const selectSubcontractEditContractContractParamTemplate = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.contractParamTemplate),
)

export const selectSubcontractEditContractParamTemplate = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.paramTemplate),
)

export const selectSubcontractParamTemplates = createSelector(
    selectSubcontractEditSelectedContractTemplate,
    selectSubcontractEditContractContractParamTemplate,
    selectSubcontractEditContractParamTemplate,
    (selectedContractTemplate, contractParamTemplate, paramTemplate) => ({
        selectedContractTemplate,
        contractParamTemplate,
        paramTemplate,
    }),
)

export const selectSetSubcontractParcelRentData = createSelector(
    selectSubcontractEditEditedCpd,
    selectSubcontractEditEditedParcel,
    selectSubcontractEditContract,
    selectSubcontractEditContractRentDrupozCollection,
    selectSubcontractEditContractRentUnknownCollection,
    (editedCPD, editedParcel, contract, contractRentDrupozCollection, contractRentUnknownCollection) => ({
        editedCPD,
        editedParcel,
        contract,
        contractRentDrupozCollection,
        contractRentUnknownCollection,
    }),
)

export const selectSubontractEditSignatureYear = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.signatureYear),
)

export const selectSubcontractEditContractAndSignatureYear = createSelector(
    selectSubcontractEditContract,
    selectSubontractEditSignatureYear,
    (contract, signatureYear) => ({ contract, signatureYear }),
)

export const selectSubcontractEditParcelsAndCpds = createSelector(
    selectSubcontractEditParcels,
    selectSubcontractEditContractCpds,
    (parcels, cpds) => ({
        parcels,
        cpds,
    }),
)

export const selectSubcontractEditContractOwnerParams = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.ownerparams),
)

export const selectSubcontractEditExpirationDate = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.expirationDate),
)

export const selectSubcontractEditBeforeSetOwners = createSelector(
    selectSubcontractEditTenants,
    selectSubcontractEditContractOwnerParams,
    (tenants, ownerparams) => ({
        tenants,
        ownerparams,
    }),
)

export const selectSubcontractEditContractWithSubjectsAndParcels = createSelector(
    selectSubcontractEditContract,
    selectSubcontractEditOwners,
    selectSubcontractEditTenants,
    selectSubcontractEditParcels,
    selectSubcontractEditSelectedContractTemplate,
    (contract, owners, tenants, parcels, selectedContractTemplate) => ({
        contract,
        owners,
        tenants,
        parcels,
        selectedContractTemplate,
    }),
)

export const selectSubcontractEditDurationPeriodState = createSelector(
    selectSubcontractEditContract,
    (contract) => ({
        indefinite: contract?.indefinite,
        reusedPreviousExpiration: contract?.reusedPreviousExpiration,
        durationMonths: toDisplayUnits(contract?.durationMonths, contract?.durationMonthsUnit),
        durationMonthsUnit: contract?.durationMonthsUnit,
        durationToDate: contract?.durationToDate,
    })
)

export const selectSubcontractEditRelatedContracts = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.relatedContracts),
)
export const selectSubcontractEditSignatureDocuments = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.signatureDocuments),
)
export const selectSubcontractEditSignatureDocumentsGroup = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.signatureDocumentsGroup),
)

export const selectSubcontractEditAttachments = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.attachments),
)
export const selectSubcontractEditAttachmentsLoading = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.attachmentsLoading),
)

export const selectSubcontractEditAttachmentsState = createSelector(
    selectSubcontractEditAttachments,
    selectSubcontractEditAttachmentsLoading,
    (attachments, attachmentsLoading) => ({
        attachments,
        attachmentsLoading,
    }),
)

export const selectSubcontractEditPayoutToId = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.payoutToId),
)

export const selectSubcontractEditGroupId = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.groupId),
)

export const selectSubcontractEditContractCreateIndividualDocuments = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.createIndividualDocuments),
)

export const selectSubcontractEditIsEdit = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isEdit),
)

export const selectSubcontractEditDocumentsState = createSelector(
    selectSubcontractEditRelatedContracts,
    selectSubcontractEditSignatureDocuments,
    selectSubcontractEditSignatureDocumentsGroup,
    selectSubcontractEditOwners,
    selectSubcontractEditContractId,
    selectSubcontractEditPayoutToId,
    selectSubcontractEditGroupId,
    selectSubcontractEditContractCreateIndividualDocuments,
    selectSubcontractEditIsEdit,
    selectSubcontractEditAttachments,
    selectSubcontractEditAttachmentsLoading,
    (relatedContracts, signatureDocuments, signatureDocumentsGroup, owners, contractId, payoutToId, groupId, createIndividualDocuments, canAddDocuments, attachments, attachmentsLoading) => ({
        relatedContracts,
        signatureDocuments,
        signatureDocumentsGroup,
        owners,
        contractId,
        payoutToId,
        groupId,
        createIndividualDocuments,
        canAddDocuments,
        attachments,
        attachmentsLoading,
    }),
)

export const selectSubcontractEditContractToEconomicYear = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.toEconomicYear)
)

export const selectSubcontractEditContractSignatureDate = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.signatureDate),
)

export const selectSubcontractEditTemplates = createSelector(
    selectSubcontractEditContractContractParamTemplate,
    selectSubcontractEditParamTemplates,
    selectSubcontractEditIsLoading,
    (contractParamTemplate, contractParamTemplates, contractIsLoading) => ({
        contractParamTemplate,
        contractParamTemplates,
        contractIsLoading,
    }),
)

export const selectSubcontractEditContractFurtherRentPossible = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.furtherRentPossible),
)

export const selectSubcontractEditContractReqNewAppendixAfterReprice = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.reqNewAppendixAfterReprice),
)

export const selectSubcontractEditContractTenantHasPreemption = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.tenantHasPreemption),
)

export const selectSubcontractOptionalParagraph = createSelector(
    selectSubcontractEditContractFurtherRentPossible,
    selectSubcontractEditContractReqNewAppendixAfterReprice,
    selectSubcontractEditContractTenantHasPreemption,
    (furtherRentPossible, reqNewAppendixAfterReprice, tenantHasPreemption) => ({
        furtherRentPossible,
        reqNewAppendixAfterReprice,
        tenantHasPreemption,
    }),
)

export const selectSubcontractEditContractVersionType = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.versionType),
)

export const selectSubcontractParcelRentedAcreage = createSelector(
    selectSubcontractEditEditedParcel,
    selectSubcontractEditTenants,
    selectSubcontractEditEditedCpd,
    (editedParcel, tenants, editedCpd) => ({
        editedParcel,
        tenants,
        editedCpd,
    }),
)

export const selectSubcontractEditPaymentsFromEconomicYear = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.paymentsFromEconomicYear),
)

export const selectSubcontractEditCounterpartyNumber = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.counterpartyNumber),
)

export const selectSubcontractEditIsRentTypeDefined = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isRentTypeDefined),
)

export const selectSubcontractEditSameLVAndKatuze = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.sameLVAndKatuze),
)

export const selectSubcontractEditIsEditingContract = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.isEditingContract),
)
export const selectSubcontractEditEditSessionKey = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.editSessionKey),
)

export const selectSubcontractEditBooleans = createSelector(
    selectSubcontractEditIsLoading,
    selectSubcontractEditIsEdit,
    selectSubcontractEditIsAppendix,
    selectSubcontractEditIsSuccessive,
    selectSubcontractEditIsRentTypeDefined,
    selectSubcontractEditIsEditingContract,
    selectSubcontractEditSameLVAndKatuze,
    (isLoading, isEdit, isAppendix, isSuccessive, isRentTypeDefined, isEditingContract, sameLVAndKatuze) => ({
        isLoading,
        isEdit,
        isAppendix,
        isSuccessive,
        isRentTypeDefined,
        isEditingContract,
        sameLVAndKatuze,
    }),
)

export const selectSubcontractEditSuccessiveSubjects = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.successiveSubjects),
)

export const selectSubcontractEditContractParamTemplates = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.contractParamTemplates),
)

export const selectSubcontractEditObjects = createSelector(
    selectSubcontractEditSuccessiveSubjects,
    selectSubcontractEditParcels,
    selectSubcontractEditOwners,
    selectSubcontractEditTenants,
    selectSubcontractEditContractParamTemplates,
    selectSubcontractEditContractRentDrupozCollection,
    selectSubcontractEditContractRentUnknownCollection,
    selectContractEditDrupozs,
    (successiveSubjects, parcels, owners, tenants, contractParamTemplates, contractRentDrupozCollection, contractRentUnknownCollection, drupozs) => ({
        successiveSubjects,
        parcels,
        owners,
        tenants,
        contractParamTemplates,
        contractRentDrupozCollection,
        contractRentUnknownCollection,
        drupozs,
    }),
)

export const selectSubcontractEditFirstPossibleTerminationDate = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.firstPossibleTerminationDate),
)

export const selectSubcontractEditRunningTask = createSelector(
    selectSubcontractEditFeature,
    feature => feature?.runningTask,
)
export const selectSubcontractEditPreviousOwnersCount = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.previousOwnersCount),
)
export const selectSubcontractEditIndividual = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.individual),
)

export const selectSubcontractEdiSelectedTab = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.selectedTab),
)

export const selectSubcontractEditSignatureYear = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.signatureYear),
)

export const selectSubcontractEditTotalAcreage = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.totalAcreage),
)

export const selectSubcontractEditContractDocumentNote = createSelector(
    selectSubcontractEditContract,
    feature => (feature?.documentNote)
)

export const selectSubcontractEditNote = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.note),
)

export const selectSubcontractEditParcelsOrder = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.parcelsOrder),
)

export const selectSubcontractEditData = createSelector(
    selectSubcontractEditFirstPossibleTerminationDate,
    selectSubcontractEditIndividual,
    selectSubcontractEditPreviousOwnersCount,
    selectSubcontractEditRunningTask,
    selectSubcontractEdiSelectedTab,
    selectSubcontractEditSelectedContractTemplate,
    selectSubcontractEditSignatureYear,
    selectSubcontractEditTotalAcreage,
    selectSubcontractEditContractDocumentNote,
    selectSubcontractEditParcelsOrder,
    (firstPossibleTerminationDate, individual, previousOwnersCount, runningTask,
     selectedTab, selectedContractTemplate, signatureYear, totalAcreage, documentNote, parcelsOrder) => ({
        firstPossibleTerminationDate,
        individual,
        previousOwnersCount,
        runningTask,
        selectedTab,
        selectedContractTemplate,
        signatureYear,
        totalAcreage,
        documentNote,
        parcelsOrder,
    }),
)

export const selectSubcontractEditEditedOwnerships = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.editedOwnerships),
)

export const selectSubcontractEditEditedObjects = createSelector(
    selectSubcontractEditEditedCpd,
    selectSubcontractEditEditedOwnerships,
    selectSubcontractEditEditedParcel,
    (editedCPD, editedOwnerships, editedParcel) => ({
        editedCPD,
        editedOwnerships,
        editedParcel,
    }),
)

export const selectSubcontractEditContractTenantIds = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.tenantIds),
)

export const selectSubcontractEditContractParcelIds = createSelector(
    selectSubcontractEditContract,
    contract => (contract?.parcelIds),
)

export const selectSubcontractEditIds = createSelector(
    selectSubcontractEditContractOwnerIds,
    selectSubcontractEditContractTenantIds,
    selectSubcontractEditContractParcelIds,
    (ownerIds, tenantIds, parcelIds) => ({
        ownerIds,
        tenantIds,
        parcelIds,
    }),
)
export const selectSubcontractEditContractParcelPrice = createSelector(
    selectSubcontractEditContract,
    selectSubcontractEditEditedCpd,
    selectSubcontractEditEditedParcel,
    (contract, editedCPD, editedParcel) => ({
        contract, editedCPD, editedParcel,
    }),
)

export const selectSubcontractEditContractNoticePeriod = createSelector(
    selectSubcontractEditContract,
    (contract) => ({
        noticePeriodMonths: toDisplayUnits(contract?.noticePeriodMonths, contract?.noticePeriodMonthsUnit),
        noticePeriodMonthsUnit: contract?.noticePeriodMonthsUnit,
        indefinite: contract?.indefinite,
    })
)

export const selectSubcontractEditLvInfo = createSelector(
    selectSubcontractEditParcels,
    selectSubcontractEditSameLVAndKatuze,
    (parcels, sameLVAndKatuze) => {
        if (sameLVAndKatuze && parcels?.length) {
            return parcels[0].katuzeNazev + ', LV ' + parcels[0].lv
        } else {
            return null
        }
    },
)

export const selectSubcontractEditDrupozs = createSelector(
    selectSubcontractEditFeature,
    feature => (feature?.drupozs),
)

export const selectSubcontractEditTaxTenant = createSelector(
    selectSubcontractEditDrupozs,
    selectSubcontractEditEditedParcel,
    selectSubcontractEditEditedCpd,
    (drupozs, editedParcel, editedCpd) => ({
        drupozs,
        editedParcel,
        editedCpd,
    }),
)